import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../assets/scss/ghExperience/ghExperience.scss";

import GhxpIntro from "./components/GhxpIntro";
import GhxpOpening from "./components/GhxpOpening";
import GhxpConcept from "./components/GhxpConcept";
import GhxpMeeting from "./components/GhxpMeeting";
import GhxpStep from "./components/GhxpStep";
import GhxpDone from "./components/GhxpDone";
import GhxpMemory from "./components/GhxpMemory";
import GhxpArrows from "./components/GhxpArrows";
import { Signature } from "./components/GhxpUi";
import { DEFAULT_FORM_DATA, STEPS, applyMasks, clearFields, validateFields } from "./components/ghxpFormConfig";
import { EVENT_TIME } from "./components/ghxpEvent";
import { registerGhExperience } from "src/services/registerGhExperience";

import logoGhxp from "../../assets/img/ghExperience/logo-ghxp26.svg";
import yearBackground from "../../assets/img/ghExperience/year-2026.svg";

// Slides: 0 abertura | 1 conceito | 2 encontro | 3 a 6 etapas do formulário | 7 envio concluído | 8 lembrança
const OPENING = 0;
const CONCEPT = 1;
const MEETING = 2;
const FIRST_STEP = 3;
const LAST_STEP = FIRST_STEP + STEPS.length - 1;
const DONE = LAST_STEP + 1;
const MEMORY = DONE + 1;
const RAIL_COUNT = DONE; // o trilho numera da abertura à última etapa

const LANGUAGES = ["pt", "en", "es"];

// Transição em portal (como o emotion-agency.com): o slide que entra se abre num círculo a partir do ponto
// do clique (ou do centro), com um anel de luz na borda; o que sai é empurrado para frente e escurece.
const TRANSITION_MS = 1200;
const LOW_POWER_TRANSITION_MS = 220;
const WHEEL_THRESHOLD = 30;
const SWIPE_THRESHOLD = 60;

// Convite fechado: toca sozinho por INTRO_AUTO_MS e então as metades se abrem em INTRO_MS.
const INTRO_AUTO_MS = 3600;
const INTRO_MS = 1300;
const LOW_POWER_INTRO_AUTO_MS = 900;
const LOW_POWER_INTRO_MS = 250;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const FINE_POINTER_QUERY = "(pointer: fine)";
const PORTRAIT_QUERY = "(max-aspect-ratio: 4/5)";
const MOBILE_QUERY = "(max-width: 767px)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    // Safari/iOS até a versão 13 só conhecem addListener.
    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

// Aparelhos fracos, economia de dados ou preferência por menos movimento: sem animações pesadas e sem autoplay.
function useLowPower() {
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [constrained] = useState(() => {
    const nav = navigator;
    const connection = nav.connection || {};
    return (
      (nav.deviceMemory && nav.deviceMemory <= 2) ||
      (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) ||
      connection.saveData === true ||
      /(^|[^45])[23]g$/.test(connection.effectiveType || "")
    );
  });
  return reducedMotion || constrained;
}

function useInterFont() {
  useEffect(() => {
    if (document.getElementById("ghxp-font")) return;
    const add = (attrs) => {
      const link = document.createElement("link");
      Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value));
      document.head.appendChild(link);
    };
    add({ rel: "preconnect", href: "https://fonts.googleapis.com" });
    add({ rel: "preconnect", href: "https://fonts.gstatic.com", crossorigin: "" });
    add({
      id: "ghxp-font",
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap",
    });
  }, []);
}

// Título da aba e idioma do documento enquanto a LP estiver aberta.
function useDocumentMeta(title, lang) {
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const previous = { title: document.title, lang: document.documentElement.lang, viewport: viewport?.content };
    // env(safe-area-inset-*) só tem valor no iPhone com viewport-fit=cover.
    if (viewport) viewport.content = "width=device-width, initial-scale=1, viewport-fit=cover";
    return () => {
      document.title = previous.title;
      document.documentElement.lang = previous.lang;
      if (viewport) viewport.content = previous.viewport;
    };
  }, []);

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = lang === "pt" ? "pt-BR" : lang;
  }, [title, lang]);
}

// Contagem regressiva ao vivo (dias, horas, minutos e segundos), atualizada a cada segundo enquanto a abertura está na tela.
function useCountdown(active) {
  const compute = () => {
    const diff = Math.max(0, EVENT_TIME - Date.now());
    const days = Math.floor(diff / 86400000);
    return {
      over: diff <= 0,
      today: diff > 0 && days === 0,
      days,
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
    };
  };
  const [value, setValue] = useState(compute);

  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => setValue(compute()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);

  return value;
}

// Teclado virtual aberto no celular: a faixa de foto encolhe e a barra de ações deixa de flutuar.
function useKeyboardOpen(enabled) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!enabled || !viewport) return undefined;
    const onResize = () => setOpen(viewport.height < window.innerHeight * 0.7);
    viewport.addEventListener("resize", onResize);
    return () => viewport.removeEventListener("resize", onResize);
  }, [enabled]);

  return enabled && open;
}

// Parallax leve das fotos e luz que segue o cursor (só com ponteiro fino): um listener, um frame por vez.
function useParallax(rootRef, enabled) {
  useEffect(() => {
    const root = rootRef.current;
    if (!enabled) {
      ["--ghxp-mx", "--ghxp-my", "--ghxp-px", "--ghxp-py"].forEach((name) => root.style.removeProperty(name));
      return undefined;
    }
    let frame = 0;
    const onMove = (event) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        root.style.setProperty("--ghxp-mx", ((event.clientX / window.innerWidth) * 2 - 1).toFixed(3));
        root.style.setProperty("--ghxp-my", ((event.clientY / window.innerHeight) * 2 - 1).toFixed(3));
        root.style.setProperty("--ghxp-px", `${Math.round(event.clientX)}px`);
        root.style.setProperty("--ghxp-py", `${Math.round(event.clientY)}px`);
      });
    };
    root.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      root.removeEventListener("mousemove", onMove);
      window.cancelAnimationFrame(frame);
    };
  }, [rootRef, enabled]);
}

// Áreas roláveis dentro de um slide (formulário, cartão final, carrosséis) têm prioridade sobre a troca de slide.
function scrollableBlocks(target, direction) {
  const area = target.closest?.("[data-scrollable]");
  if (!area) return false;
  if (area.dataset.scrollable === "x") return false;
  if (direction > 0) return area.scrollTop + area.clientHeight < area.scrollHeight - 1;
  return area.scrollTop > 0;
}

function LanguageSwitch() {
  const { t, i18n } = useTranslation();

  return (
    <div className="ghxpLang" role="group" aria-label={t("ghxp_language")}>
      {LANGUAGES.map((lng) => (
        <button
          key={lng}
          type="button"
          lang={lng}
          className={i18n.resolvedLanguage === lng ? "is-active" : ""}
          aria-pressed={i18n.resolvedLanguage === lng}
          onClick={() => i18n.changeLanguage(lng)}
        >
          {lng.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function GhExperience() {
  useInterFont();

  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  useDocumentMeta(t("ghxp_page_title"), lang);

  const rootRef = useRef(null);
  const lockRef = useRef(false);
  const timerRef = useRef(0);
  const pendingFocusRef = useRef(null);
  const currentRef = useRef(OPENING);
  const wheelDeltaRef = useRef(0);
  const pointerRef = useRef(null); // último toque/clique: o portal nasce ali
  const touchStartRef = useRef(null);

  const lowPower = useLowPower();
  const finePointer = useMediaQuery(FINE_POINTER_QUERY);
  const portrait = useMediaQuery(PORTRAIT_QUERY);
  const mobile = useMediaQuery(MOBILE_QUERY);

  const [current, setCurrent] = useState(OPENING);
  const [stage, setStage] = useState(null); // { from, to } enquanto dois slides se movem
  const [settled, setSettled] = useState(false);
  const [maxReached, setMaxReached] = useState(OPENING);
  const [interacted, setInteracted] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [transitions, setTransitions] = useState(0);

  // O convite chega fechado a cada visita e se abre sozinho; qualquer interação pula a sequência.
  const [intro, setIntro] = useState("closed");
  // A sequência (keyframes + relógio) só corre com a fonte pronta e a aba visível, nos mesmos frames.
  const [introPlaying, setIntroPlaying] = useState(false);
  const introRef = useRef(intro);
  introRef.current = intro;

  const countdown = useCountdown(current === OPENING && intro === "open");
  const keyboardOpen = useKeyboardOpen(mobile && current >= FIRST_STEP && current <= LAST_STEP);
  useParallax(rootRef, finePointer && !lowPower && intro === "open");

  // Centro do portal: o último clique/toque (se recente) ou um ponto dado; raio = distância ao canto mais distante.
  const placePortal = useCallback((point) => {
    const root = rootRef.current;
    if (!root) return;
    const w = root.clientWidth;
    const h = root.clientHeight;
    const recent = pointerRef.current && Date.now() - pointerRef.current.at < 700 ? pointerRef.current : null;
    const x = point ? point.x : recent ? recent.x : w / 2;
    const y = point ? point.y : recent ? recent.y : h / 2;
    const r = Math.ceil(Math.max(Math.hypot(x, y), Math.hypot(w - x, y), Math.hypot(x, h - y), Math.hypot(w - x, h - y))) + 40;
    root.style.setProperty("--ghxp-cx", `${Math.round(x)}px`);
    root.style.setProperty("--ghxp-cy", `${Math.round(y)}px`);
    root.style.setProperty("--ghxp-r", `${r}px`);
  }, []);

  // Abre o convite: o portal nasce do selo (onde o anel de progresso termina).
  const openIntro = useCallback(() => {
    if (introRef.current !== "closed") return;
    const seal = rootRef.current?.querySelector(".ghxpIntro__sealRing");
    if (seal) {
      const rect = seal.getBoundingClientRect();
      const rootRect = rootRef.current.getBoundingClientRect();
      placePortal({ x: rect.left + rect.width / 2 - rootRect.left, y: rect.top + rect.height / 2 - rootRect.top });
    } else {
      placePortal();
    }
    setIntro((state) => (state === "closed" ? "opening" : state));
  }, [placePortal]);

  // Enquanto o convite está fechado, a navegação fica travada e um relógio abre o convite sozinho
  // (pausado com a aba em segundo plano, para não abrir sem ninguém ver). Ao abrir, a abertura entra em cascata.
  useEffect(() => {
    if (intro === "closed") {
      lockRef.current = true;
      let remaining = lowPower ? LOW_POWER_INTRO_AUTO_MS : INTRO_AUTO_MS;
      let startedAt = 0;
      let timer = 0;
      let ready = false;
      let cancelled = false;
      const play = () => {
        if (cancelled || !ready || document.hidden || timer) return;
        startedAt = Date.now();
        timer = window.setTimeout(openIntro, remaining);
        setIntroPlaying(true);
      };
      const pause = () => {
        if (!timer) return;
        window.clearTimeout(timer);
        timer = 0;
        remaining = Math.max(0, remaining - (Date.now() - startedAt));
        setIntroPlaying(false);
      };
      const onVisibility = () => (document.hidden ? pause() : play());
      // Espera a Inter (no máximo 400 ms) para o texto não trocar de fonte no meio da sequência.
      const fonts = document.fonts ? document.fonts.ready : Promise.resolve();
      Promise.race([fonts, new Promise((resolve) => setTimeout(resolve, 400))]).then(() => {
        ready = true;
        play();
      });
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
        document.removeEventListener("visibilitychange", onVisibility);
      };
    }
    if (intro === "opening") {
      const timer = window.setTimeout(() => setIntro("open"), lowPower ? LOW_POWER_INTRO_MS : INTRO_MS);
      return () => window.clearTimeout(timer);
    }
    lockRef.current = false;
    const timer = window.setTimeout(() => setSettled(true), 120);
    return () => window.clearTimeout(timer);
  }, [intro, lowPower, openIntro]);

  // Troca de slide em dois tempos: o destino fica visível na posição de partida e só então se move.
  // Só os dois slides envolvidos animam; os demais ficam ocultos e fora da árvore de acessibilidade.
  const slideTo = useCallback(
    (index) => {
      if (lockRef.current || index === currentRef.current) return;
      lockRef.current = true;
      window.clearTimeout(timerRef.current);
      placePortal();
      const from = currentRef.current;
      currentRef.current = index;
      setSettled(false);
      setStage({ from, to: index });
      setInteracted(true);
      setTransitions((n) => n + 1);
      setMaxReached((max) => Math.max(max, index));

      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          setCurrent(index);
          timerRef.current = window.setTimeout(() => {
            setStage(null);
            setSettled(true);
            lockRef.current = false;
            wheelDeltaRef.current = 0;
          }, (lowPower ? LOW_POWER_TRANSITION_MS : TRANSITION_MS) + 60);
        })
      );
    },
    [lowPower, placePortal]
  );

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (submitError) setSubmitError(false);
  };

  const failValidation = (stepErrors, step, deferred = false) => {
    setErrors((prev) => ({ ...prev, ...stepErrors }));
    setShaking(true);
    window.setTimeout(() => setShaking(false), 320);
    const first = step.fields.find((f) => stepErrors[f.name]);
    // Se a etapa com erro ainda vai entrar na tela, o foco espera a transição terminar.
    if (deferred) pendingFocusRef.current = first.name;
    else document.getElementById(`ghxp-${first.name}`)?.focus({ preventScroll: true });
  };

  const submit = async () => {
    // Revalida tudo: o trilho permite voltar e editar etapas anteriores.
    for (let i = 0; i < STEPS.length; i += 1) {
      const stepErrors = validateFields(formData, STEPS[i].fields.map((f) => f.name), lang);
      if (Object.keys(stepErrors).length > 0) {
        const jump = currentRef.current !== FIRST_STEP + i;
        if (jump) slideTo(FIRST_STEP + i);
        failValidation(stepErrors, STEPS[i], jump);
        return;
      }
    }
    setIsSending(true);
    setSubmitError(false);
    const response = await registerGhExperience({
      Name: formData.name,
      Email: formData.email,
      Phone: formData.phone,
      Birthday: formData.birthDate,
      Address: formData.address,
      Cep: formData.zipCode,
      Children: formData.children,
      Team: formData.team,
      // Não é salvo: define o idioma do e-mail de confirmação.
      Language: lang,
    });
    setIsSending(false);
    lockRef.current = false;

    if (response.success) {
      slideTo(DONE);
      return;
    }
    // E-mail já confirmado: volta para a 1ª etapa com o aviso no campo de e-mail.
    if (response.status === 409) {
      const jump = currentRef.current !== FIRST_STEP;
      if (jump) slideTo(FIRST_STEP);
      failValidation({ email: "ghxp_error_duplicate" }, STEPS[0], jump);
      return;
    }
    setSubmitError(true);
  };

  // Botões "Confirmar presença" das cenas: a página é uma experiência, então levam ao próximo slide
  // (do encontro, o próximo slide já é a 1ª etapa do formulário).
  const start = () => {
    if (!lockRef.current && !isSending && currentRef.current < FIRST_STEP) slideTo(currentRef.current + 1);
  };

  // Avança: cenas em ordem; nas etapas valida os campos antes de seguir; depois do envio, a lembrança.
  const next = () => {
    const index = currentRef.current;
    if (lockRef.current || isSending) return undefined;
    if (index < FIRST_STEP) return slideTo(index + 1);
    if (index === DONE) return slideTo(MEMORY);
    if (index > LAST_STEP) return undefined;

    const step = STEPS[index - FIRST_STEP];
    const stepErrors = validateFields(formData, step.fields.map((f) => f.name), lang);
    if (Object.keys(stepErrors).length > 0) {
      failValidation(stepErrors, step);
      return undefined;
    }
    if (index === LAST_STEP) return submit();
    return slideTo(index + 1);
  };

  // Etapa opcional (endereço): pula sem validar e descarta o que foi digitado nela,
  // para um CEP incompleto não travar o envio no final.
  const skip = () => {
    const index = currentRef.current;
    const step = STEPS[index - FIRST_STEP];
    if (lockRef.current || isSending || !step?.optional) return;
    const names = step.fields.map((f) => f.name);
    setFormData((prev) => clearFields(prev, names));
    setErrors((prev) => names.reduce((result, name) => ({ ...result, [name]: undefined }), prev));
    if (index === LAST_STEP) submit();
    else slideTo(index + 1);
  };

  const back = () => {
    const index = currentRef.current;
    if (isSending) return;
    if (index === MEMORY) slideTo(DONE);
    else if (index > OPENING && index < DONE) slideTo(index - 1);
  };

  // Volta para a abertura; depois de um envio, limpa o formulário para um novo cadastro.
  const restart = () => {
    if (isSending || lockRef.current || currentRef.current === OPENING) return;
    const finishedFlow = currentRef.current >= DONE;
    setErrors({});
    setSubmitError(false);
    slideTo(OPENING);
    if (finishedFlow) {
      // Espera a tela final sair para não desmontar as fotos no meio da animação.
      window.setTimeout(() => {
        setFormData(DEFAULT_FORM_DATA);
        setMaxReached(OPENING);
      }, (lowPower ? LOW_POWER_TRANSITION_MS : TRANSITION_MS) + 60);
    }
  };

  // Imagens pesadas só são montadas quando o slide está a até dois passos de distância (ou em movimento).
  const near = (index) =>
    index <= Math.max(current, maxReached) + 2 || (stage !== null && (index === stage.from || index === stage.to));

  // Slide na tela: o atual ou um dos dois que se movem na troca.
  const visible = (index) => index === current || (stage !== null && (index === stage.from || index === stage.to));

  // Trocar o idioma no meio do formulário: reaplica as máscaras (BR x internacional) e limpa os erros desses campos.
  const langRef = useRef(lang);
  useEffect(() => {
    if (langRef.current === lang) return;
    langRef.current = lang;
    setFormData((prev) => applyMasks(prev, lang));
    setErrors((prev) => ({ ...prev, phone: undefined, zipCode: undefined }));
  }, [lang]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  // O trilho só leva a slides já alcançados (uma etapa só é alcançada depois de validar a anterior).
  const reachable = (index) => index <= Math.max(maxReached, FIRST_STEP);
  const goTo = (index) => {
    if (reachable(index) && !isSending) slideTo(index);
  };

  // A página inteira funciona como slides: trava a rolagem do documento enquanto a LP estiver aberta.
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  // Só o slide atual fica acessível (Tab, leitores de tela); evita pular etapas sem validar.
  useEffect(() => {
    rootRef.current.querySelectorAll("[data-slide-index]").forEach((slide) => {
      slide.inert = Number(slide.dataset.slideIndex) !== current;
    });

    if (!settled) return;
    if (current >= FIRST_STEP && current <= LAST_STEP) {
      const pending = pendingFocusRef.current;
      pendingFocusRef.current = null;
      const target = pending || (finePointer ? STEPS[current - FIRST_STEP].fields[0].name : null);
      if (target) document.getElementById(`ghxp-${target}`)?.focus({ preventScroll: true });
    } else if (current === DONE) {
      // Leitores de tela e teclado seguem para a confirmação em vez de ficarem no body.
      document.getElementById("ghxp-done-title")?.focus({ preventScroll: true });
    }
  }, [current, settled, finePointer]);

  // Rolagem do mouse/trackpad, teclado e gesto de deslizar.
  const nextRef = useRef(next);
  const backRef = useRef(back);
  const slideToRef = useRef(slideTo);
  nextRef.current = next;
  backRef.current = back;
  slideToRef.current = slideTo;

  useEffect(() => {
    const root = rootRef.current;

    // Com o convite fechado, qualquer rolagem, tecla ou toque abre o convite.
    const introClosed = () => {
      if (introRef.current === "open") return false;
      openIntro();
      return true;
    };

    const onWheel = (event) => {
      // Lista de seleção aberta: a rolagem é dela, não troca de slide.
      if (event.target.closest?.(".ghxpSelect.is-open")) {
        if (!scrollableBlocks(event.target, Math.sign(event.deltaY))) event.preventDefault();
        return;
      }
      const direction = Math.sign(event.deltaY);
      if (!direction || scrollableBlocks(event.target, direction)) return;
      event.preventDefault();
      if (introClosed() || lockRef.current) return;

      wheelDeltaRef.current += event.deltaY;
      if (Math.abs(wheelDeltaRef.current) >= WHEEL_THRESHOLD) {
        const goForward = wheelDeltaRef.current > 0;
        wheelDeltaRef.current = 0;
        if (goForward) nextRef.current();
        else backRef.current();
      }
    };

    const onKeyDown = (event) => {
      const tag = event.target.tagName;
      if (["INPUT", "SELECT", "TEXTAREA"].includes(tag)) return;
      // Seleção própria do formulário: as setas abrem e percorrem a lista.
      if (event.defaultPrevented || event.target.closest?.('[role="combobox"]')) return;
      if (introRef.current !== "open") {
        if (["ArrowDown", "PageDown", "ArrowRight", "Enter", " ", "Escape"].includes(event.key)) {
          event.preventDefault();
          openIntro();
        }
        return;
      }
      if (["ArrowDown", "PageDown"].includes(event.key)) {
        event.preventDefault();
        nextRef.current();
      } else if (["ArrowUp", "PageUp"].includes(event.key)) {
        event.preventDefault();
        backRef.current();
      } else if (event.key === "Home" && currentRef.current <= MEETING) {
        event.preventDefault();
        slideToRef.current(OPENING);
      } else if (event.key === "End" && currentRef.current <= MEETING) {
        event.preventDefault();
        slideToRef.current(MEETING);
      }
    };

    const onTouchStart = (event) => {
      const tag = event.target.tagName;
      touchStartRef.current = ["INPUT", "SELECT", "TEXTAREA"].includes(tag)
        ? null
        : { x: event.touches[0].clientX, y: event.touches[0].clientY };
    };

    const onTouchEnd = (event) => {
      const startPoint = touchStartRef.current;
      if (!startPoint) return;
      touchStartRef.current = null;
      if (event.target.closest?.(".ghxpSelect.is-open")) return;
      const touch = event.changedTouches[0];
      const deltaY = startPoint.y - touch.clientY;
      const deltaX = startPoint.x - touch.clientX;
      // Gestos horizontais pertencem aos carrosséis.
      if (Math.abs(deltaX) > Math.abs(deltaY)) return;
      if (Math.abs(deltaY) < SWIPE_THRESHOLD || scrollableBlocks(event.target, Math.sign(deltaY))) return;
      if (introClosed()) return;
      if (deltaY > 0) nextRef.current();
      else backRef.current();
    };

    const onPointerDown = (event) => {
      const rect = root.getBoundingClientRect();
      pointerRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top, at: Date.now() };
    };

    root.addEventListener("pointerdown", onPointerDown, { passive: true });
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openIntro]);

  const slideClass = (index, extra) => {
    const classes = ["ghxpSlide", extra];
    if (index === current) classes.push("is-active");
    else classes.push(index < current ? "is-past" : "is-next");
    const moving = stage && (index === stage.from || index === stage.to);
    if (index === current || moving) classes.push("is-visible");
    if (moving) classes.push("is-moving", index === stage.to ? "is-to" : "is-from");
    if (index === current && settled) classes.push("is-settled");
    return classes.join(" ");
  };

  const inForm = current >= FIRST_STEP && current <= LAST_STEP;
  const pillVisible = current === CONCEPT || current === MEETING;
  const finished = current >= DONE;
  const rootClass = [
    lowPower ? "is-low-power" : "",
    keyboardOpen ? "is-keyboard" : "",
    inForm ? "is-form" : "",
    finished ? "is-finished" : "",
    intro === "closed" ? "is-intro" : "",
    intro === "opening" ? "is-intro-opening" : "",
  ].join(" ");

  const railLabel = (index) => {
    if (index === OPENING) return t("ghxp_nav_opening");
    if (index === CONCEPT) return t("ghxp_nav_concept");
    if (index === MEETING) return t("ghxp_nav_meeting");
    return t("ghxp_step_label", { current: String(index - FIRST_STEP + 1).padStart(2, "0"), total: String(STEPS.length).padStart(2, "0") });
  };

  return (
    <div id="ghExperience" ref={rootRef} className={rootClass}>
      {/* Padrão "2026" único, fixo atrás de todos os slides */}
      <img className="ghxpBg" src={yearBackground} alt="" aria-hidden="true" />

      {/* Convite fechado: abertura interativa */}
      {intro !== "open" && <GhxpIntro state={intro} playing={introPlaying || intro === "opening"} onOpen={openIntro} />}

      <header className={`ghxpHeader ${current > OPENING ? "is-compact" : ""}`}>
        <button type="button" className="ghxpHeader__brand" onClick={restart} aria-label={t("ghxp_back_home")}>
          <img src={logoGhxp} alt="#GHXP 26" width="490" height="88" />
        </button>
        <LanguageSwitch />
        <Signature className="ghxpHeader__signature" />
      </header>

      {/* Barra de progresso (celular) */}
      <div
        className={`ghxpProgress ${finished ? "is-hidden" : ""}`}
        role="progressbar"
        aria-label={t("ghxp_progress_label")}
        aria-valuemin={1}
        aria-valuemax={RAIL_COUNT}
        aria-valuenow={Math.min(current, LAST_STEP) + 1}
      >
        <span style={{ width: `${((Math.min(current, LAST_STEP) + 1) / RAIL_COUNT) * 100}%` }} />
      </div>

      <main className="ghxpDeck">
        {/* Abertura — convite digital e Projeto Visual, pág. 8 */}
        <section data-slide-index={OPENING} className={slideClass(OPENING, "ghxpOpening")}>
          <GhxpOpening
            countdown={countdown}
            onStart={start}
            onNext={next}
            showHint={!interacted}
            active={current === OPENING}
            settled={current === OPENING && settled}
            lowPower={lowPower}
            portrait={portrait}
          />
        </section>

        {/* Cena 1 — Mais longe, juntos. (págs. 6 e 7) */}
        <section data-slide-index={CONCEPT} className={slideClass(CONCEPT, "ghxpConcept ghxpScene")}>
          <GhxpConcept
            onStart={start}
            near={near(CONCEPT)}
            warm={maxReached >= CONCEPT || (current === OPENING && settled)}
            active={current === CONCEPT}
            settled={current === CONCEPT && settled}
            visible={visible(CONCEPT)}
            lowPower={lowPower}
            spine={mobile && portrait}
          />
        </section>

        {/* Cena 2 — Relacionamento, conversa e gastronomia (págs. 2 e 14) */}
        <section data-slide-index={MEETING} className={slideClass(MEETING, "ghxpMeeting")}>
          <GhxpMeeting
            onStart={start}
            active={current === MEETING}
            settled={current === MEETING && settled}
            warm={maxReached >= CONCEPT}
            lowPower={lowPower}
            portrait={portrait}
          />
        </section>


        {/* Etapas do formulário — pág. 21 */}
        {STEPS.map((step, i) => (
          <section
            key={step.titleKey}
            data-slide-index={FIRST_STEP + i}
            className={slideClass(FIRST_STEP + i, "ghxpFormSlide")}
          >
            <GhxpStep
              step={step}
              stepIndex={i}
              totalSteps={STEPS.length}
              formData={formData}
              errors={errors}
              isSending={isSending}
              submitError={submitError}
              shaking={shaking && current === FIRST_STEP + i}
              near={near(FIRST_STEP + i)}
              active={current === FIRST_STEP + i}
              settled={current === FIRST_STEP + i && settled}
              lowPower={lowPower}
              portrait={portrait}
              onChange={setField}
              onNext={next}
              onBack={back}
              onSkip={skip}
            />
          </section>
        ))}

        {/* Envio concluído — pág. 17 */}
        <section data-slide-index={DONE} className={slideClass(DONE, "ghxpDoneSlide ghxpScene")}>
          <GhxpDone
            formData={formData}
            active={current === DONE}
            settled={current === DONE && settled}
            near={near(DONE)}
            lowPower={lowPower}
            portrait={portrait}
            onRestart={restart}
            onNext={next}
          />
        </section>

        {/* Lembrança — pág. 16; só existe depois do envio */}
        {maxReached >= DONE && (
          <section data-slide-index={MEMORY} className={slideClass(MEMORY, "ghxpMemory")}>
            <GhxpMemory onRestart={restart} active={current === MEMORY} near={near(MEMORY)} />
          </section>
        )}
        {/* Anel de luz na borda do portal (troca de slide e abertura do convite) */}
        {(stage || intro === "opening") && <span key={`${transitions}-${intro}`} className="ghxpPortal" aria-hidden="true" />}
      </main>

      {/* Trilho numerado (desktop) */}
      <nav className={`ghxpRail ${finished ? "is-hidden" : ""}`} aria-label={t("ghxp_nav_label")}>
        {Array.from({ length: RAIL_COUNT }, (_, index) => (
          <button
            key={index}
            type="button"
            className={index === current ? "is-active" : index < current ? "is-done" : ""}
            disabled={!reachable(index)}
            aria-current={index === current ? "step" : undefined}
            aria-label={railLabel(index)}
            data-label={railLabel(index)}
            onClick={() => goTo(index)}
          >
            {String(index + 1).padStart(2, "0")}
          </button>
        ))}
      </nav>

      {/* CTA persistente nas cenas de conteúdo (celular): também leva ao próximo slide */}
      <div className={`ghxpPill ${pillVisible ? "is-visible" : ""}`}>
        <button type="button" className="ghxpButton" onClick={start} tabIndex={pillVisible ? 0 : -1}>
          <span>{t("ghxp_cta")}</span>
          <GhxpArrows variant="solid" size="sm" className="ghxpButton__icon" />
        </button>
      </div>
    </div>
  );
}

export default GhExperience;
